import { EAIClient } from "../../services/EAIClient";
import { Utils } from "../utils";
import CONFIG from "Config";
import { establishPlatformApiSession, refreshPlatformApiSession } from "../../services/platform/platform-session";
import { renewCircuitBreaker } from "../../services/RenewCircuitBreaker";

export interface AuthResult {
  idToken: string;
  accessToken: string;
  expiresIn?: number;
  tokenType?: string;
  email?: string;
}

export interface AuthError {
  error: string;
  errorDescription?: string;
  /**
   * Legacy auth0-js spelling. New code should emit `errorDescription`; read
   * either spelling through {@link getAuthErrorMessage} rather than directly.
   */
  description?: string;
}

/** Shown when an auth error carries no usable description of its own. */
export const GENERIC_AUTH_ERROR = "Something went wrong. Please try again.";

/**
 * Reads the human-readable message off an {@link AuthError}, tolerating both
 * spellings: FusionAuth emits `errorDescription`, auth0-js emits `description`.
 *
 * The single place either field is read. Always returns a displayable string —
 * callers used to read `err.description` directly, which rendered the literal
 * text "undefined" for every FusionAuth failure. See EAI-5638.
 */
export function getAuthErrorMessage(err?: AuthError | null, fallback: string = GENERIC_AUTH_ERROR): string {
  return err?.errorDescription || err?.description || fallback;
}

export abstract class BaseAuth {
  protected EAIClient: EAIClient;

  constructor() {
    this.EAIClient = new EAIClient();
  }

  // Métodos que podem ser sobrescritos pelas classes filhas
  login(username: string, password: string, onError: (err: AuthError) => void): void {
    throw new Error('login method must be implemented by subclass');
  }

  loginRedirect(): void {
    throw new Error('loginRedirect method must be implemented by subclass');
  }

  authorizeSSOConnection(connection: string): void {
    throw new Error('authorizeSSOConnection method must be implemented by subclass');
  }

  resetPassword(email: string, onError: (err: AuthError) => void, onSuccess: (resp: any) => void): void {
    throw new Error('resetPassword method must be implemented by subclass');
  }

  handleAuthentication(): void {
    throw new Error('handleAuthentication method must be implemented by subclass');
  }

  handleLogout(callback: () => void): void {
    throw new Error('handleLogout method must be implemented by subclass');
  }

  postLoginRedirect() {
    let redirect = localStorage.getItem("post_login_redirect");
    redirect = redirect === "/callback" || redirect === "/login" ? "/" : redirect;
    location.replace(redirect || Utils.getLinkBase());
    localStorage.removeItem("post_login_redirect");
  }

  handleTokenRenew = async () => {
    try {
      // EAIClient.renewTokens owns the cross-tab lock, in-flight coalescing,
      // and the write of fresh values to localStorage. Nothing to do here on
      // the storage side; a null response means another tab already handled it.
      const resp = await this.EAIClient.renewTokens();

      // Cutover: keep the platform-api session alive alongside eai's, or
      // migrated modules start 401ing partway through a long-lived tab.
      // Best-effort, so it cannot fail the renewal it rides along with.
      await refreshPlatformApiSession();

      return resp ? resp.data.data : null;
    } catch (error) {
      console.error("Token renewal failed:", error);
      // If renewal fails, clear storage and redirect to login
      Utils.clearLocalstorage();
      location.replace(CONFIG.paths.base + "login");
      throw error;
    }
  };

  getAuthMethod = async (path: string) => {
    const resp = await this.EAIClient.getAuthMethod(path);
    return resp.data.data;
  };

  getUniversityUrl = async (email: string) => {
    const resp = await this.EAIClient.getUniversityUrl(email);
    return resp.data.data;
  };

  isAuthenticated = () => {
    // Keys to check in localstorage.
    const localStorageValidation = {
      current_user: "json",
      expires_at: "number",
      is_superadmin: "boolean",
      // school_list is a comma separated list, just check that it isn't null
      school_list: "",
      chosen_school_info: "json",
    };

    let areValuesGood = true;

    Object.keys(localStorageValidation).forEach(key => {
      const value = localStorage.getItem(key);
      if (value === null) {
        areValuesGood = false;
      } else {
        switch (localStorageValidation[key]) {
          case "json":
            try {
              JSON.parse(value);
            } catch (e) {
              console.error(`Bad json value in localstorage key: ${key}`, { value });
              areValuesGood = false;
            }
            break;
          case "boolean":
            if (!["true", "false"].includes(value.toLowerCase())) {
              console.error(`Bad boolean value in localstorage for key ${key}`, { value });
              areValuesGood = false;
            }
            break;
          case "number":
            if (isNaN(parseInt(value))) {
              console.error(`Bad number value in localstorage for key ${key}`, { value });
              areValuesGood = false;
            }
            break;
          default:
            break;
        }
      }
    });

    if (!areValuesGood) {
      // If a value in localStorage is bad clear them and
      //  have the user log in again to set them
      Utils.clearLocalstorage();
      return false;
    }

    // Otherwise, check the expiry listed in localstorage
    // The EAIClient will still log them out if their cookies have expired
    const expiresAtVal = localStorage.getItem("expires_at");
    if (!expiresAtVal) {
      return false;
    }
    const expiresAt = parseInt(expiresAtVal);

    return expiresAt ? new Date(expiresAt * 1000) > new Date() : false;
  };

  protected handleAuthSuccess = async (authResult: AuthResult) => {
    const selectedSchoolId = localStorage.getItem("chosen_school");
    const email = authResult.email;

    // If chosen_school is set, pass it along with tokens to allow api to do login events
    // If not set or if not in user's school list, api will use the first school in
    //   the user's list (which we will then default to as well)
    try {
      const resp = await this.EAIClient.logIn(
        authResult.idToken,
        authResult.accessToken,
        selectedSchoolId,
        email,
        window.location.hostname
      );

      renewCircuitBreaker.reset();
      Utils.setStorageValues(resp.data.data, async () => {
        if (selectedSchoolId && authResult.accessToken) {
          try {
            await establishPlatformApiSession({
              accessToken: authResult.accessToken,
              email,
              schoolId: selectedSchoolId,
            });
          } catch (error) {
            // Suppress errors from platform-api login for now; proceed with legacy flow.
            console.warn("Failed to initialize session in platform-api", error);
          }
        }

        this.postLoginRedirect();
      });
    } catch (err: any) {
      const data = err?.response?.data;
      if (data?.result?.error === "UserNotActive") {
        localStorage.setItem(
          "authentication_error",
          "You are not currently configured in Enflux. Please ask your Enflux administrator to add you to Enflux"
        );
      }

      return location.replace(CONFIG.paths.base + "login");
    }
  };

  protected handleAuthError = (err: AuthError) => {
    const errorMessage = getAuthErrorMessage(err, "Authentication Result Invalid");
    localStorage.setItem("authentication_error", errorMessage);
    return location.replace(CONFIG.paths.base + "login");
  };
} 